/** Filtros da tela de histórico de apontamentos. Os rótulos viajam junto dos ids para o chip
 *  poder mostrar o nome sem consultar o servidor. */

/** `week` e `month` são dinâmicos: as datas saem de "hoje" a cada consulta, não ficam guardadas. */
export type PeriodoAtalho = 'week' | 'month' | 'custom';

export interface TicketLogFilterParams {
  user_ids?: string[];
  user_labels?: string[];
  customer_ids?: string[];
  customer_labels?: string[];
  period?: PeriodoAtalho;
  /** `AAAA-MM-DD`. Só tem efeito quando `period === 'custom'`. */
  date_from?: string;
  date_to?: string;
}

export const PERIODO_PADRAO: PeriodoAtalho = 'week';

export const TICKET_LOG_FILTERS_STORAGE_KEY = 'ticket-log-history-filters';

/**
 * ⚠️ Tem que diferir da `storageKey` que o `patron-filter-widget` usava aqui
 * (`ticket-log-history-find-options`): a tabela `saved_filters` é compartilhada e a `key` é o
 * único separador. O widget grava um `find_options` de outro formato, e misturar os dois faria
 * o `SavedFilterService` ler `params: undefined`.
 */
export const TICKET_LOG_SAVED_FILTERS_KEY = 'ticketcenter-ticket-log-history';

export const PERIODO_LABELS: Record<PeriodoAtalho, string> = {
  week: 'Esta semana',
  month: 'Este mês',
  custom: 'Período',
};

/** `Date` → `AAAA-MM-DD`, o formato que a API espera nos filtros de data. */
export function formatarISO(data: Date): string {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

/**
 * Segunda-feira da semana de `hoje`.
 *
 * `getDay()` devolve 0 no DOMINGO, então `getDay() - 1` daria -1 e jogaria o domingo para a
 * segunda seguinte — o único dia em que a conta ingênua erra. `(getDay() + 6) % 7` mapeia
 * segunda→0 … domingo→6.
 */
function segundaDaSemana(hoje: Date): Date {
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  inicio.setDate(inicio.getDate() - ((inicio.getDay() + 6) % 7));
  return inicio;
}

/**
 * Datas concretas do recorte. `null` quando o modo é `custom` e falta uma das duas — é o que a
 * tela usa para bloquear a busca. A semana cobre os sete dias, inclusive os que ainda não
 * chegaram (aparecem zerados no gráfico, que é a leitura pedida).
 */
export function resolvePeriodo(
  filters: TicketLogFilterParams,
  hoje: Date
): { from: string; to: string } | null {
  const periodo = filters.period ?? PERIODO_PADRAO;

  if (periodo === 'week') {
    const inicio = segundaDaSemana(hoje);
    const fim = new Date(inicio);
    fim.setDate(inicio.getDate() + 6);
    return { from: formatarISO(inicio), to: formatarISO(fim) };
  }

  if (periodo === 'month') {
    const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    // Dia 0 do mês seguinte é o último dia deste — resolve 28/29/30/31 sem tabela.
    const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    return { from: formatarISO(inicio), to: formatarISO(fim) };
  }

  if (!filters.date_from || !filters.date_to) return null;
  return { from: filters.date_from, to: filters.date_to };
}

export function isTicketLogFilterValid(filters: TicketLogFilterParams, hoje: Date): boolean {
  return !!filters.user_ids?.length && resolvePeriodo(filters, hoje) !== null;
}
